/* eslint-disable  react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Box, MenuItem, SelectProps, SwitchProps } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import SelectField from "components/Forms/SelectField";
import apiRequest from "utils/apiRequestWithErrorCode";
import { useRuleContext } from "../context/ruleContext";

import { Skeleton } from "@material-ui/lab";
import { Storage } from "@dashboard-v3/api";
import { getCloudStorage as getStorage } from "pages/Rules/Form/context/ruleSelectors";

type AiPostActionState = {
  scopeOptions: string[];
  loading: boolean;
  isEnabled: boolean;
};

export function AiPostAction() {
  const { t } = useTranslation("rules");
  const { enqueueSnackbar } = useSnackbar();
  const ruleContext = useRuleContext();
  const storage = getStorage(ruleContext.state.rule);
  const [state, setState] = useState<AiPostActionState>(initState(storage));

  const updateState = (update: Partial<AiPostActionState>) => {
    setState(prev => ({ ...prev, ...update }));
  };

  useEffect(() => {
    async function getAiScopes() {
      try {
        const res = await apiRequest<string[]>("GET", "/mxhero-ai/scopes");
        updateState({ scopeOptions: res });
      } catch (e) {
        enqueueSnackbar(t("moreActions.aiAction.getScopesError"), {
          variant: "error",
        });
      } finally {
        updateState({ loading: false });
      }
    }

    if (!state.scopeOptions.length) {
      getAiScopes();
    }
  }, []);

  const onEnableScopes: SwitchProps["onChange"] = e => {
    const { checked } = e.target;

    updateState({ isEnabled: checked });

    ruleContext.dispatch({
      type: "updateMoreActions",
      payload: { aiAction: { scope: checked ? "default" : null } },
    });
  };

  const onScopeChange: SelectProps["onChange"] = e => {
    const { value } = e.target;

    ruleContext.dispatch({
      type: "updateMoreActions",
      payload: { aiAction: { scope: value } },
    });
  };

  if (state.loading) {
    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1}>
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="text" width="80%" height={16} />
          </Box>
        </Box>

        {state.isEnabled && (
          <Box mt={2} mb={2}>
            <Skeleton
              variant="rect"
              width="100%"
              height={56}
              style={{ borderRadius: 4 }}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <SwitchField
        label={t("moreActions.aiAction.label")}
        description={t("moreActions.aiAction.description")}
        value={state.isEnabled}
        onChange={onEnableScopes}
      />
      {state.isEnabled && (
        <Box mt={2} mb={2}>
          <SelectField
            name="scope"
            label={t("moreActions.aiAction.scopeLabel")}
            options={state.scopeOptions}
            value={storage.onStorageSuccess?.aiAction?.scope || ""}
            onChange={onScopeChange}
            renderOption={scope => (
              <MenuItem key={scope} value={scope}>
                {scope}
              </MenuItem>
            )}
          />
        </Box>
      )}
    </>
  );
}

function initState(storage: Storage) {
  const { onStorageSuccess } = storage;

  return (): AiPostActionState => ({
    isEnabled: Boolean(onStorageSuccess?.aiAction),
    loading: false,
    scopeOptions: [],
  });
}
